import React from "react";
//#region redux
import { useDispatch } from "react-redux";
import { supportTicketConfirmed, supportRated } from "store/actions/supportActions";
//#endregion
//#region types
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
import { FormHandles } from "models/propTypes/genericFormProps";
import { CommentsFormTypes } from "models/formTypes";
import { SupportIssueProps } from "models/propTypes/supportIssuePropTypes";
import { StyleEnums } from "models/enums";
//#endregion
//#region components
import { EntityCardContent, ChatView, useCheckRelations } from "components/common";
import SupportIssueFooter from "./footer";
import SupportIssueTopCardContent from "./_partials/topCardContent";
import useSupportMessageResolveConfirm from "./useConfirm";
import { useSupportService } from "serviceHooks";
//#endregion
import { FileHelper, LocalizationHelperInstance, UtilsHelper } from "utils/helpers";
import { toSupportCommentItems } from "./supportCommentItems";
import { getSupportAttachmentTypes } from "./supportAttachmentTypes";

const SupportIssue: React.FC<SupportIssueProps> = (props) => {
  const supportService = useSupportService();
  const { getIsRevisor } = useCheckRelations();
  const { supportIssue, reloadHandler, statusIndicatorDisplayType, enableResolvedCaseConfirmation } = props;
  const formRef = React.useRef<FormHandles>(null);
  const dispatch = useDispatch();

  const [isFormFilled, setIsFormFilled] = React.useState<boolean>(false);

  const lastComment = supportIssue?.comments?.[supportIssue?.comments.length - 1];
  const [feedbackWasSubmitted, setFeedbackWasSubmitted] = React.useState<boolean>(lastComment?.hasRating ?? false);

  const { onConfirm, getCanClear } = useSupportMessageResolveConfirm();

  const canClear = getCanClear(
    props.supportIssue.state,
    props.supportIssue.isUnread,
    props.supportIssue.isUnreadByCurrentUser
  );

  React.useEffect(() => {
    return () => {
      if (canClear) {
        dispatch(supportTicketConfirmed(props.supportIssue.id));
        onConfirm(supportIssue.id);
      }
    };
  }, [props.supportIssue.state, props.supportIssue.isUnreadByCurrentUser]);

  function getComments() {
    return toSupportCommentItems(
      supportIssue?.comments,
      LocalizationHelperInstance.getMessageForAllLocales(TranslationKeys.SupportIssue.NoMessage)
    );
  }

  const getImageContent = (id: number | string) => {
    return supportService.getAttachment(supportIssue?.id as number, id as number);
  };

  const onFileClick = (id: number | string) => {
    supportService.downloadAttachment(supportIssue?.id as number, id as number).then((res) => {
      FileHelper.download(res);
    });
  };

  const onSubmitForm = () => {
    return (
      (formRef.current &&
        formRef.current.submit(
          undefined,
          undefined,
          undefined,
          undefined,
          TranslationKeys.SupportIssue.MarkAsDoneHint,
          StyleEnums.DisplayType.Info,
          false
        )) ||
      new Promise<void>((resolve) => resolve())
    );
  };

  const handleSubmit = (values: CommentsFormTypes.CommentWithFile) => {
    return Promise.all([
      !UtilsHelper.isEmpty(values.explanation) || values.filesToUpload?.length
        ? supportService.addCommentAsync(
            supportIssue!.id,
            supportService.convertToSupportCommentPost(
              UtilsHelper.isEmpty(values.explanation)
                ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SupportIssue.NoMessage)
                : values.explanation,
              values.filesToUpload
            )
          )
        : Promise.resolve(),
      values.markAsResolved && supportIssue ? supportService.markAsResolved(supportIssue.id) : Promise.resolve(),
    ]);
  };

  const getMainContainerTitle = () => {
    if (supportIssue?.state !== WintSuperkollAPIModel.SupportIssueState.Closed) return undefined;

    return LocalizationHelperInstance.getFormattedMessage(
      supportIssue.isLocked
        ? TranslationKeys.SupportIssue.SupportIssueIsLocked
        : TranslationKeys.SupportIssue.SupportIssueIsResolved
    );
  };

  const getMainContainerSubTitle = () => {
    // isLocked wins over the ordinary closed/reopenable subtitle below: it is a specific reason
    // ("your byrå can no longer answer this"), not the generic "you closed this, still reopenable"
    // hint — which is also actively wrong here, since IsReadOnly blocks replies regardless of state.
    if (supportIssue?.isLocked) {
      return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SupportIssue.AgencyLockedBanner);
    }

    if (supportIssue?.state !== WintSuperkollAPIModel.SupportIssueState.Closed || supportIssue.isReadOnly)
      return undefined;

    return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SupportIssue.SupportIssueReopenMessage);
  };

  const onFormUpdate = (values: CommentsFormTypes.CommentWithFile) => {
    setIsFormFilled(!UtilsHelper.isEmpty(values.explanation) || !!values.filesToUpload?.length);
  };

  const onSubmitFeedback = () => {
    setFeedbackWasSubmitted(true);
    dispatch(supportRated(supportService.getUpdatedSupportIssueWithRatingFlag(supportIssue)));
  };

  return (
    <EntityCardContent
      topTodoCard={
        <SupportIssueTopCardContent
          supportIssue={supportIssue!}
          reloadHandler={reloadHandler}
          statusIndicatorDisplayType={statusIndicatorDisplayType}
        />
      }
      todoMainSection={
        <ChatView
          ref={formRef}
          parentId={supportIssue.id}
          comments={getComments()}
          getImageContent={getImageContent}
          onFileClick={onFileClick}
          initialValues={{ postWithoutPicture: true }}
          handleSubmit={handleSubmit}
          commentOrFileRequired
          onFormUpdate={onFormUpdate}
          onSubmitFeedback={onSubmitFeedback}
          feedbackWasSubmitted={feedbackWasSubmitted}
          showRatingComponent={
            supportIssue.state === WintSuperkollAPIModel.SupportIssueState.AtCustomer && supportIssue.canRate
          }
          titleContainerHeader={getMainContainerTitle()}
          titleContainerSubHeader={getMainContainerSubTitle()}
          isReadonly={supportIssue.isReadOnly}
          hasMultipleRecipients={supportService.getConversationParticipantCount(supportIssue) > 2}
          isConfidential={supportIssue.isConfidential}
          attachmentTypes={getSupportAttachmentTypes(getIsRevisor())}
          isOwnedByAgency={supportIssue.isOwnedByAgency}
        />
      }
      footer={
        <SupportIssueFooter
          supportIssue={supportIssue!}
          reloadHandler={props.reloadHandler}
          onSubmitTicket={onSubmitForm}
          disableSuccessMessages
          feedbackWasSubmitted={feedbackWasSubmitted}
          isFormFilled={isFormFilled}
          enableResolvedCaseConfirmation={enableResolvedCaseConfirmation}
        />
      }
    ></EntityCardContent>
  );
};

export default SupportIssue;
